// pages/FormViewerPage/hooks/useFormViewer/computed.ts — Derived useMemo values
import { useMemo } from 'react';
import { computeResolvedVariantCombinations, computeDerivedOptions, computeCompositeFallback, computeDisplayOptions, computeShippingCost, computeCodFee, computeSortedPaymentKeys, getSimpleModeDefaultCombination } from '../../services/formViewerService';
import { computeExcludedProvinces, resolveGroupId, resolveRegionPrice } from '../../../../utils/regionPricing';
import type { FormViewerState } from './state';

// Terapkan harga region ke kombinasi: kunci 1 grup menang tanpa provinsi,
// else provinsi customer → grup → override harga.
function applyRegionPrice<T extends { sellingPrice: number; regionPrices?: Record<string, number> }>(combo: T, form: any, province?: string): T {
    const groupId = resolveGroupId(form, province);
    if (!groupId) return combo;
    const price = resolveRegionPrice(combo.sellingPrice, combo.regionPrices, groupId);
    return price === combo.sellingPrice ? combo : { ...combo, sellingPrice: price };
}

export function useFormViewerComputed(s: FormViewerState, isSimpleEmbed: boolean = false) {
    const { form, productOptionsOverride, selectedShippingKey, selectedEverproRate, selectedBiteshipRate, addressData, selectedPaymentKey, selectedOptions, variantStock, curComboRef, quantity } = s;

    const resolvedVariantCombinations = useMemo(() =>
        computeResolvedVariantCombinations(form?.variantCombinations || [], productOptionsOverride)
            .map(c => applyRegionPrice(c, form, addressData.province)),
        [form, productOptionsOverride, addressData.province]
    );

    const derivedOptions = useMemo(() => computeDerivedOptions(form), [form]);

    const compositeFallback = useMemo(() => computeCompositeFallback(derivedOptions), [derivedOptions]);

    const displayOptions = useMemo(() =>
        computeDisplayOptions(form, derivedOptions, productOptionsOverride, compositeFallback),
        [form, derivedOptions, productOptionsOverride, compositeFallback]
    );

    const currentCombination = useMemo(() => {
        if (!form?.variantCombinations || !form.variantCombinations.length) return null;
        const isSimpleMode = form?.addressInputMode === 'simple' || isSimpleEmbed || (form as any)?.thankYouPage?.consultationMode;
        const defaultFallback = isSimpleMode
            ? (getSimpleModeDefaultCombination(form.variantCombinations) || form.variantCombinations[0])
            : form.variantCombinations[0];

        let found;
        if (compositeFallback) {
            const combined = compositeFallback.buildCombinedValue(selectedOptions);
            found = combined ? form.variantCombinations.find(vc => Object.values(vc.attributes || {}).join(' - ') === combined) || defaultFallback || null : defaultFallback || null;
        } else {
            found = form.variantCombinations.find(vc => Object.entries(selectedOptions).every(([k, v]) => vc.attributes[k] === v)) || defaultFallback || null;
        }
        return found ? applyRegionPrice(found, form, addressData.province) : null;
    }, [form, selectedOptions, compositeFallback, form?.addressInputMode, isSimpleEmbed, addressData.province]);

    curComboRef.current = currentCombination;

    const EXCLUDED_PROVINCES = useMemo(() => computeExcludedProvinces(form), [form?.productBlockedProvinces, form?.productRegionGroups, form?.regionLock, (form as any)?.shippingSettings?.allowedProvinces]);

    const currentVariantStock = useMemo(() => {
        if (!form?.stockCountdownSettings?.active) return 0;
        const key = currentCombination ? Object.values(currentCombination.attributes || {}).join(' / ') : 'Produk Tunggal';
        return variantStock[key] || 0;
    }, [form?.stockCountdownSettings?.active, variantStock, currentCombination]);

    const shippingCost = useMemo(() =>
        computeShippingCost(form, selectedShippingKey, selectedEverproRate, selectedBiteshipRate, addressData, currentCombination, quantity),
        [form, selectedShippingKey, selectedEverproRate, selectedBiteshipRate, addressData.province, form?.addressInputMode, currentCombination, quantity]
    );

    const codFee = useMemo(() =>
        computeCodFee(form, selectedPaymentKey, currentCombination, shippingCost, quantity),
        [form, selectedPaymentKey, currentCombination, shippingCost, quantity]
    );

    const sortedPaymentKeys = useMemo(() =>
        computeSortedPaymentKeys(form, addressData, shippingCost, s.lowPerfNoCod, (selectedEverproRate as any)?.price ?? shippingCost),
        [form, addressData.province, shippingCost, s.lowPerfNoCod, (selectedEverproRate as any)?.price]
    );

    return {
        resolvedVariantCombinations, derivedOptions, compositeFallback, displayOptions,
        currentCombination, EXCLUDED_PROVINCES, currentVariantStock, shippingCost, codFee,
        sortedPaymentKeys,
    };
}

export type FormViewerComputed = ReturnType<typeof useFormViewerComputed>;
